import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ExternalLink } from "lucide-react";
import Seo from "../components/Seo";

const KELSEY_PORTRAIT_FALLBACK = "/assets/artist/kelsey_portrait.jpg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ARTIST_HERO_MP4 = `${BACKEND_URL}/api/media/artist_hero.mp4`;
const ARTIST_HERO_WEBM = `${BACKEND_URL}/api/media/artist_hero.webm`;

export default function ArtistPage() {
  const [photos, setPhotos] = useState([]);
  const [products, setProducts] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    fetch("/gallery.json").then(r => r.json()).then((d) => setPhotos(d.artist || [])).catch(() => {});
    api.get("/settings/artist-video").then((r) => setVideoUrl(r.data.video_url)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const portrait = photos[0] || KELSEY_PORTRAIT_FALLBACK;
  const otherPhotos = photos.slice(1);

  return (
    <div data-testid="artist-page" className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
      <Seo
        title="The Artist — Kelsey-Lynn"
        description="Every Kaiya towel is an original monotype print by Toronto-based visual artist Kelsey-Lynn. Meet the artist behind the practice."
        path="/artist"
      />
      {/* VIDEO HERO — always render; falls back to local studio video when admin video URL is unset */}
      <div
        className="image-soft aspect-[21/9] md:aspect-[21/8] mb-12 md:mb-16 overflow-hidden relative"
        data-testid="artist-video-hero-wrapper"
        style={{ background: "var(--kaiya-bg-2)" }}
      >
        <video
          autoPlay muted loop playsInline
          preload="auto"
          poster={`${BACKEND_URL}/api/media/artist_hero_poster.jpg`}
          className="w-full h-full object-cover"
        >
          {videoUrl ? (
            <source src={videoUrl} />
          ) : (
            <>
              {/* WebM first: broad support in Chrome/Firefox/Edge/Android */}
              <source src={ARTIST_HERO_WEBM} type="video/webm" />
              {/* MP4 fallback: Safari + iOS */}
              <source src={ARTIST_HERO_MP4} type="video/mp4" />
            </>
          )}
        </video>
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 bg-gradient-to-t from-black/60 to-transparent">
          <div className="font-serif-display text-3xl md:text-5xl" style={{ color: "var(--kaiya-bg)" }}>Creation in motion.</div>
        </div>
      </div>

      {/* PORTRAIT + BIO */}
      <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-start mb-20">
        <div className="md:col-span-5">
          {portrait && (
            <div className="image-soft aspect-[3/4] overflow-hidden mb-6 md:sticky md:top-32" style={{ background: "var(--kaiya-bg-2)" }} data-testid="artist-portrait">
              <img src={portrait} alt="Kelsey-Lynn" className="w-full h-full object-cover" />
            </div>
          )}
        </div>

        <div className="md:col-span-7">
          <div className="label-eyebrow mb-3">The artist</div>
          <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-6 whitespace-nowrap">
            Kelsey-Lynn.
          </h1>
          <a
            href="https://www.instagram.com/kelseyltc"
            target="_blank" rel="noreferrer"
            data-testid="artist-ig-link"
            className="inline-flex items-center gap-2 text-sm underline underline-offset-4 mb-10"
            style={{ color: "var(--kaiya-primary)" }}
          >
            @kelseyltc <ExternalLink size={13} />
          </a>

          <div className="space-y-5 text-base md:text-lg leading-relaxed opacity-90">
            <p>
              Kelsey-Lynn, a <strong>Toronto-based artist</strong>, has been <strong>immersed in creation her entire life</strong> — first through dance as a child, and now through printmaking.
            </p>
            <p>
              Blending movement and action, she has spent years developing a <em style={{ color: "var(--kaiya-primary)" }}>unique language of line and color</em>, drawing inspiration from process-based artists.
            </p>
            <p>
              For Kelsey, <strong>creation is never static</strong>. She has long struggled with the idea of a &ldquo;finished&rdquo; piece, always seeking a process that <em>mirrors the ever-evolving nature of life itself</em>.
            </p>
            <p>
              We hope her art inspires you to <strong>embrace creation and beauty</strong> — both on and off the mat with your KaiyaYoga towel.
            </p>
          </div>

        </div>
      </div>

      {/* UNIFIED ARTIST GRID — 8 tiles, alternating art ↔ person, artist portrait last */}
      <section className="mb-20 md:mb-28 -mt-8 md:-mt-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5" data-testid="artist-gallery">
          {/* 1. ART — video (art in motion) */}
          <div className="image-soft aspect-[3/4] overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
            <video
              data-testid="artist-studio-video"
              src="/assets/artist/studio_video.mp4"
              className="w-full h-full object-cover block"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster="/assets/artist/monotype_press.jpg"
            />
          </div>

          {/* 2. THE ARTIST — swapped up from position 8 */}
          <div className="image-soft aspect-[3/4] overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
            <img src="/assets/artist/kelsey_portrait.jpg" alt="Kelsey-Lynn in her Toronto studio" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>

          {/* 3. ART — palette tools */}
          <div className="image-soft aspect-[3/4] overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
            <img src="/assets/artist/palette_tools.jpg" alt="Paints, palette knife and printmaking tools" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>

          {/* 4. PERSON — Kelsey (gallery.json[2]) */}
          {otherPhotos[1] && (
            <div data-testid="artist-photo-1" className="image-soft aspect-[3/4] overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
              <img src={otherPhotos[1]} alt="Kelsey in the studio" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          )}

          {/* 5. ART — palette swatches */}
          <div className="image-soft aspect-[3/4] overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
            <img src="/assets/artist/palette_swatches.jpg" alt="Colour swatches drying on the studio counter" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>

          {/* 6. PERSON — Kelsey (gallery.json[3]) */}
          {otherPhotos[2] && (
            <div data-testid="artist-photo-2" className="image-soft aspect-[3/4] overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
              <img src={otherPhotos[2]} alt="Kelsey holding a large monotype" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          )}

          {/* 7. ART — monotype on the press */}
          <div className="image-soft aspect-[3/4] overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
            <img src="/assets/artist/monotype_press.jpg" alt="An in-progress monotype on the printing press" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>

          {/* 8. PERSON — Kelsey with a print (swapped down from position 2) */}
          {otherPhotos[0] && (
            <div data-testid="artist-photo-0" className="image-soft aspect-[3/4] overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
              <img src={otherPhotos[0]} alt="Kelsey with a fresh monotype" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          )}
        </div>
      </section>

      {/* SHOP YOUR TOWEL */}
      <div className="border-t pt-16" style={{ borderColor: "var(--kaiya-border)" }}>
        <div className="text-center mb-12">
          <div className="label-eyebrow mb-3">Shop</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-none">Your Towel</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {products.map((p) => (
            <Link
              key={p.id}
              to={`/product/${p.slug}`}
              data-testid={`artist-print-${p.slug}`}
              className="group block"
            >
              <div className="image-soft aspect-[3/4] mb-4 overflow-hidden" style={{ background: "var(--kaiya-bg-2)" }}>
                <img
                  src={p.images?.[0]}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="label-eyebrow mb-1">Monotype by @kelseyltc</div>
                  <h3 className="font-serif-display text-2xl leading-tight">&apos;{p.name}&apos;</h3>
                </div>
                <div className="font-serif-display text-lg shrink-0">${p.price.toFixed(2)}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
